import type { Field, FieldADTValue, RecordLikeProtocol } from '@lighthouse/core'
import { Order } from '@lighthouse/core'
import { fieldConvertValue, getRecordPoolKey, type RecordPoolMap, useApplicationContext, useAtomData } from '@lighthouse/shared'
import type { WritableAtom } from 'jotai'
import { useCallback, useMemo } from 'react'

interface MultiDimensionalConfig {
    rowDimension: string
    columnDimension: string
    rowDimensionSortField?: string
    rowDimensionSortMode?: Order
    columnDimensionSortField?: string
    columnDimensionSortMode?: Order
}

/** 字段 schema 映射 */
type FieldSchema = { [key: string]: Field }

interface UseMultiDimensionalMatrixParams {
    recordPoolAtom: WritableAtom<RecordPoolMap, [RecordPoolMap | ((draft: RecordPoolMap) => void)], void>
    recordPoints: { id: string }[]
    dsId: string
    schema: FieldSchema
    config: MultiDimensionalConfig
}

export interface MatrixCell {
    records: RecordLikeProtocol[]
    rowKey: string
    colKey: string
}

export interface MatrixData {
    /** 行维度的唯一值列表 */
    rowKeys: string[]
    /** 列维度的唯一值列表 */
    colKeys: string[]
    /** 二维矩阵数据 matrix[rowKey][colKey] */
    matrix: Map<string, Map<string, MatrixCell>>
    /** 所有记录 */
    records: RecordLikeProtocol[]
}

/**
 * 从记录中提取字段值并转换为显示文本
 */
function getFieldDisplayValue(
    record: RecordLikeProtocol,
    fieldId: string,
    schema: FieldSchema,
    options?: Parameters<typeof fieldConvertValue>[1]
): string {
    const cellValue = record.content?.[fieldId]?.value
    const field = schema[fieldId]

    if (!field || cellValue === undefined || cellValue === null) {
        return ''
    }

    return fieldConvertValue({ ...field, value: cellValue } as FieldADTValue, options) || String(cellValue)
}

/**
 * 获取排序值（用于比较）
 */
function getSortValue(
    record: RecordLikeProtocol,
    sortFieldId: string | undefined,
    dimensionFieldId: string,
    schema: FieldSchema,
    options?: Parameters<typeof fieldConvertValue>[1]
): string | number {
    // 如果指定了排序字段，使用排序字段的值
    const fieldId = sortFieldId || dimensionFieldId
    const cellValue = record.content?.[fieldId]?.value
    const field = schema[fieldId]

    if (!field || cellValue === undefined || cellValue === null) {
        return ''
    }

    // 数字类型直接返回数值用于排序
    if ((field.innerType === 'NUMBER' || field.innerType === 'DATE') && typeof cellValue === 'number') {
        return cellValue
    }

    return fieldConvertValue({ ...field, value: cellValue } as FieldADTValue, options) || String(cellValue)
}

/**
 * 比较函数：支持数字和字符串
 */
function compareSortValues(a: string | number, b: string | number): number {
    if (typeof a === 'number' && typeof b === 'number') {
        return a - b
    }
    return String(a).localeCompare(String(b))
}

/**
 * 多维矩阵数据处理 Hook
 * 将记录按行维度和列维度分组，构建二维矩阵
 */
export function useMultiDimensionalMatrix(params: UseMultiDimensionalMatrixParams): MatrixData {
    const { recordPoolAtom, recordPoints, dsId, schema, config } = params
    const {
        rowDimension,
        columnDimension,
        rowDimensionSortField,
        rowDimensionSortMode,
        columnDimensionSortField,
        columnDimensionSortMode
    } = config
    const { personOptions, roleOptions, departmentOptions } = useApplicationContext()

    // 从 recordPool 获取当前视图的所有记录，保持 recordPoints 的顺序
    const records = useAtomData(
        recordPoolAtom,
        useCallback(
            pool => recordPoints.map(p => pool.get(getRecordPoolKey(dsId, p.id))).filter((r): r is RecordLikeProtocol => r !== undefined),
            [dsId, recordPoints]
        )
    )

    // 构建矩阵数据
    return useMemo(() => {
        const rowKeySet = new Set<string>()
        const colKeySet = new Set<string>()
        // 存储维度值 -> 排序值的映射
        const rowSortMap = new Map<string, string | number>()
        const colSortMap = new Map<string, string | number>()
        const matrix = new Map<string, Map<string, MatrixCell>>()
        const fieldOptions = { personOptions, roleOptions, departmentOptions }

        // 第一遍：提取所有唯一的行列值及其排序值
        for (const record of records) {
            const rowValue = getFieldDisplayValue(record, rowDimension, schema, fieldOptions)
            const colValue = getFieldDisplayValue(record, columnDimension, schema, fieldOptions)

            if (rowValue && !rowKeySet.has(rowValue)) {
                rowKeySet.add(rowValue)
                rowSortMap.set(rowValue, getSortValue(record, rowDimensionSortField, rowDimension, schema, fieldOptions))
            }
            if (colValue && !colKeySet.has(colValue)) {
                colKeySet.add(colValue)
                colSortMap.set(colValue, getSortValue(record, columnDimensionSortField, columnDimension, schema, fieldOptions))
            }
        }

        // 转换为数组并根据排序字段和排序方式排序
        const rowKeys = [...rowKeySet].sort((a, b) => {
            const result = compareSortValues(rowSortMap.get(a) ?? a, rowSortMap.get(b) ?? b)
            return rowDimensionSortMode === Order.DESC ? -result : result
        })
        const colKeys = [...colKeySet].sort((a, b) => {
            const result = compareSortValues(colSortMap.get(a) ?? a, colSortMap.get(b) ?? b)
            return columnDimensionSortMode === Order.DESC ? -result : result
        })

        // 初始化矩阵结构
        for (const rowKey of rowKeys) {
            const rowMap = new Map<string, MatrixCell>()
            for (const colKey of colKeys) {
                rowMap.set(colKey, { records: [], rowKey, colKey })
            }
            matrix.set(rowKey, rowMap)
        }

        // 第二遍：将记录分配到对应的格子
        for (const record of records) {
            const rowValue = getFieldDisplayValue(record, rowDimension, schema, fieldOptions)
            const colValue = getFieldDisplayValue(record, columnDimension, schema, fieldOptions)

            if (rowValue && colValue) {
                const cell = matrix.get(rowValue)?.get(colValue)
                if (cell) {
                    cell.records.push(record)
                }
            }
        }

        return { rowKeys, colKeys, matrix, records }
    }, [
        records,
        rowDimension,
        columnDimension,
        rowDimensionSortField,
        rowDimensionSortMode,
        columnDimensionSortField,
        columnDimensionSortMode,
        schema,
        personOptions,
        roleOptions,
        departmentOptions
    ])
}
